import { useState, useCallback, useEffect } from 'react'
import AppHeader from '../../components/AppHeader';
import AppSidebar from '../../components/AppSidebar';
import PageHeader from '../../components/PageHeader';
import ListSupportTicket from './ListSupportTicket';
import ListSubscriptionPlanTicket from './ListSubscriptionPlanTicket';
import Assign from '../../modals/Assign';
import { useForm } from 'react-hook-form';
import UsersService from '../../services/UsersService';
import EditTicketModal from '../customers/components/modals/EditTicketModal';
import { toast } from 'react-toastify';
import SubscriptionTicketService from '../../services/SubscriptionTicketService';
import ProductTicketService from '../../services/ProductTicketService';
import { statusClasses, priorityClasses, priorityOptions, statusOptions } from '../../utils/OptionUtils';
import Filters from '../../components/Filters';
import Pagination from '../../components/Pagination';
import UseDebounce from '../../hooks/UseDebounce';
import { PageLimit } from '../../utils/ConstantUtils';
import { DebounceDelay } from '../../utils/ConstantUtils'
import CreateProductTicket from '../customers/components/modals/CreateProductTicket';
import CreateSubscriptionTicketModal from '../customers/components/modals/CreateSubscriptionTicketModal';
import CustomersService from '../../services/CustomersService';
import ListAnonymousTicket from './ListAnonymousTicket';
import AnonymousTicketService from '../../services/AnonymousTicketService';
import CreateUpdateAnonymousTicket from './model/CreateUpdateAnonymousTicket';

const Tickets = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const pageLimit = PageLimit;
    const [itemsPerPage, setPageLimit] = useState(pageLimit);
    const offset = (currentPage - 1) * itemsPerPage;
    const [appliedFilters, setAppliedFilters] = useState({});
    const debouncedSearchTerm = UseDebounce(searchTerm, DebounceDelay || 100);
    const { formState: { errors }, control, handleSubmit, reset, register, setValue } = useForm();
    const [activeTab, setActiveTab] = useState('support');
    const [showStatusPriorityModal, setShowStatusPriorityModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [ticketId, setTicketId] = useState(null);
    const [columnType, setColumnType] = useState(null);
    const [type, setType] = useState(null);
    const [rows, setRows] = useState([]);
    const [showCreateProductTicket, setShowCreateProductTicket] = useState(false);
    const [showSubscriptionTicket, setShowSubscriptionTicket] = useState(false);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerId, setCustomerId] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [plan, setPlan] = useState({});
    const [subscriptionId, setSubscriptionId] = useState(null);
    const [showCreateAnonymousTicket, setShowCreateAnonymousTicket] = useState(false);

    const fetchTickets = useCallback(async () => {
        let service = null;
        if (activeTab === 'support') {
            service = ProductTicketService;
        } else if (activeTab === 'subscription') {
            service = SubscriptionTicketService;
        } else if (activeTab === 'anonymous') {
            service = AnonymousTicketService;
        }
        try {
            const response = await service.getAll({
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearchTerm,
                offset: offset,
                filters: appliedFilters,
            });
            if (response.status === 200) {
                setRows(response?.data?.data?.tickets);
                setTotalPages(response?.data?.data?.totalPages);
                setCurrentPage(response?.data?.data?.currentPage);
            }
        } catch (error) {
            console.log(error)
            toast.error(error?.response?.data?.message || error?.message || 'Error in creating customer');
        }

    }, [currentPage, itemsPerPage, debouncedSearchTerm, offset, appliedFilters, activeTab]);

    useEffect(() => {
        fetchTickets();
    }, [appliedFilters, fetchTickets]);
    
    const handleClose = () => {
        setShowStatusPriorityModal(false);
        setShowCreateProductTicket(false);
        setShowAssignModal(false);
        setShowSubscriptionTicket(false);
        setShowCreateAnonymousTicket(false);
        setSelectedTickets([]);
    };
    const handleAssign = async (id, type) => {
        setTicketId(id);
        setType(type);
        try {
            const response = await UsersService.list();
            if (response.status === 200) {
                const data = response?.data?.data || [];
                const formattedUsers = data.map(user => ({
                    value: user.id,
                    label: user.name + '-' + user.email,
                }));
                setUsersList(formattedUsers);
                setShowAssignModal(true);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };

    const onAssignSubmit = async (data) => {
        try {
            const assignedTo = data.assignee[0]?.value;
            const assignments = selectedTickets.map(ticketId => ({
                ticketId,
                assigned_to: assignedTo,
            }));
    
            let service = null;
            if (activeTab === 'support') {
                service = ProductTicketService;
            } else if (activeTab === 'subscription') {
                service = SubscriptionTicketService;
            } else if (activeTab === 'anonymous') {
                service = AnonymousTicketService;
            }
    
            let successCount = 0; 
    
            for (const assignment of assignments) {
                const response = await service.assign(assignment.ticketId, { data: { assigned_to: assignment.assigned_to } });
    
                if (response.status === 200) {
                    const responseData = response?.data?.data;
                    setRows(prevRows => prevRows.map(row => row.id === assignment.ticketId ? { ...row, assigned_to: responseData.assigned_to, assignedTo: { name: responseData.assignedTo?.name } } : row));
                    successCount++; 
                }
            }
            if (successCount > 0) {
                toast.success(`${successCount} ticket(s) assigned successfully.`);
            } else {
                toast.error("No tickets were assigned.");
            }
    
            handleClose();
            reset();
            fetchTickets();
    
        } catch (error) {
            console.log('error', error);
            toast.error(error?.message || "An error occurred");
        }
    };
    
    const handleStatusPriority = (id, type) => {
        setTicketId(id);
        setColumnType(type);
        setShowStatusPriorityModal(true);
    }
    const onStatusPrioritySubmit = async (data) => {
        try {
            let service = null;
            if (type === 'support') {
                service = ProductTicketService;
            } else if (activeTab === 'subscription') {
                service = SubscriptionTicketService;
            } else if (activeTab === 'anonymous') {
                service = AnonymousTicketService;
            }
            const response = await service.changeStatusOrPriority(ticketId, { data }, columnType);
            if (response.status === 200) {
                toast.success(response.data.message);
                setRows(prevRows => prevRows.map(row => row.id === ticketId ? { ...row, [columnType]: data[columnType] } : row));
                handleClose();
                reset();
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };
    const handleColumnChange = (record, type, tab) => {
        setTicketId(record);
        setColumnType(type);
        setType(tab);
        setShowStatusPriorityModal(true);
        reset({ [type]: record[type] });
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = (e) => {
        setCurrentPage(1);
        setPageLimit(e.target.value);
    };

    const handleFilterSubmit = (newFilters) => {
        setAppliedFilters(newFilters);
    };

    const commonFilterFields = [
        { name: 'ticket_no', placeholder: 'Ticket No', Dropdown: "yes" },
        { name: 'customer_id', placeholder: 'Customer Name', Dropdown: "yes" },
        { name: 'title', placeholder: 'Title', Dropdown: "yes" },
        { name: 'status', placeholder: 'Status', Dropdown: "yes" },
        { name: 'priority', placeholder: 'Priority', Dropdown: "yes" },
        { name: 'created_by', placeholder: 'Created By', Dropdown: "yes" },
        { name: 'assigned_to', placeholder: 'Assigned To', Dropdown: "yes" },
        { name: 'assigned_by', placeholder: 'Assigned By', Dropdown: "yes" },
        { name: 'start_date', placeholder: 'Start Date', DateDropdown: "yes"},
        { name: 'end_date', placeholder: 'End Date', DateDropdown: "yes"},
    ];

    // Product-specific filters
    const productTicketFilterFields = [
        { name: 'customer_product_id', placeholder: 'Product Name', Dropdown: "yes" },
    ];

    // Subscription-specific filters
    const subscriptionTicketFilterFields = [
        // { name: 'new_column', placeholder: 'Column Name', Dropdown: 'yes' },
    ];

    // Anonymous-specific filters
    const anonymousTicketFilterFields = [
        { name: 'product_id', placeholder: 'Product Name', Dropdown: 'yes' },
    ];

    let filterFields = [...commonFilterFields];

    if (activeTab === 'support') {
    filterFields = [...filterFields, ...productTicketFilterFields];
    } else if (activeTab === 'subscription') {
    filterFields = [...filterFields, ...subscriptionTicketFilterFields];
    } else if (activeTab === 'anonymous') {
    filterFields = [...filterFields, ...anonymousTicketFilterFields];
    }

    const fetchCustomers = useCallback(async () => {
        try {
            const response = await CustomersService.listAll();
            if (response.status === 200) {
                const data = response?.data?.data || [];
                const formattedCustomers = data.map(customer => ({
                    value: customer.id,
                    label: customer.company,
                }));
                setCustomerOptions(formattedCustomers);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    }, []);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    const handleCreateTicket = async () => {
        console.log(activeTab);
        try {
            if (activeTab === 'support') {
                setShowCreateProductTicket(true);
                return;
            }
            if (activeTab === 'subscription') {
                setShowSubscriptionTicket(true);
                if (customerId) {
                    await fetchCustomerPlans(customerId);
                }
            } if (activeTab === 'anonymous') {
                setShowCreateAnonymousTicket(true);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };
    const fetchCustomerProducts = useCallback(async (customerId) => {
        try {
            const response = await CustomersService.customerProductList(customerId);
            if (response.status === 200) {
                setProductOptions(response?.data?.data);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    }, []);

    const fetchCustomerPlans = useCallback(async (customerId) => {
        try {
            const response = await CustomersService.latestPlan(customerId);
            if (response.status === 200) {
                setPlan(response?.data?.data);
                setSubscriptionId(response?.data?.data?.subscription_id);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    }, []);

    useEffect(() => {
        if (customerId && (activeTab === 'support' || activeTab === 'anonymous')) {
            fetchCustomerProducts(customerId);
        }
        if (customerId && activeTab === 'subscription') {
            fetchCustomerPlans(customerId);
        }
    }, [customerId, fetchCustomerProducts, activeTab, fetchCustomerPlans]);

    const onTicketSubmit = async (data) => {
        try {
            let service = null;
            if (activeTab === 'support') {
                service = ProductTicketService;
            } else if (activeTab === 'subscription') {
                service = SubscriptionTicketService;
            } else if (activeTab === 'anonymous') {
                service = AnonymousTicketService;
            }            
            if (activeTab === 'subscription') {
                delete data.customer_id;
                delete data?.status;
                data.subscription_id = subscriptionId;
            }
            const response = await service.create({ data });
            if (response.status === 201) {
                toast.success(response?.data?.message);
                handleClose();
                reset();
                if (activeTab === 'support') {
                    setRows(prevRows => [...prevRows, response?.data?.data]);
                }
                if (activeTab === 'subscription') {
                    setRows(prevRows => [...prevRows, response?.data?.data]);
                }
                if (activeTab === 'anonymous') {
                    setRows(prevRows => [...prevRows, response?.data?.data]);
                }

            }
        } catch (error) {
            console.log('error', error);
            toast.error(error?.message || "An error occurred");
        }
    };

    const [selectedTickets, setSelectedTickets] = useState([]);

    // Add handlers to select/unselect tickets
    const onSelectTicket = (ticketId, checked) => {
        setSelectedTickets(prevSelected => {
            if (checked) {
                return [...prevSelected, ticketId];
            } else {
                return prevSelected.filter(id => id !== ticketId);
            }
        });
    };

    const onSelectAllTickets = (checked) => {
        if (checked) {
            // Select all ticket ids on current rows
            const allIds = rows.map(row => row.id);
            setSelectedTickets(allIds);
        } else {
            setSelectedTickets([]);
        }
    };

    return (
        <>
            <AppSidebar />
            <div className="main-content">
                <AppHeader title='Tickets' />
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 ">
                            <PageHeader
                                title="Tickets"
                                button_label1="Create"
                                buttonAttributes1={{ onClick: handleCreateTicket }}
                                className1="btn btn-sm btn-primary" 
                                extraButtons={Array.isArray(selectedTickets) &&
                                    selectedTickets.length >= 1 &&
                                    selectedTickets.length <= rows.length && (
                                            <button
                                                className="btn btn-sm btn-danger me-2"
                                                onClick={() => handleAssign(selectedTickets)}
                                            >
                                                Assign Selected
                                            </button>
                                    )}
                                />
                        </div>
                        <div className="tab-content">
                            <div className="tab-pane fade show active" id="contactsListPane" role="tabpanel" aria-labelledby="contactsListTab">
                                <div className="card">
                                    <div className="card-header">
                                        <div className="row align-items-center">
                                            <div className="col">
                                                <form>
                                                    <div className="input-group input-group-flush input-group-merge input-group-reverse">
                                                        <input
                                                            className="form-control list-search"
                                                            type="search"
                                                            placeholder="Search"
                                                            value={searchTerm}
                                                            onChange={handleSearchChange}
                                                        />
                                                        <span className="input-group-text">
                                                            <i className="fe fe-search" />
                                                        </span>
                                                    </div>
                                                </form>
                                            </div>
                                            <div className="col-auto me-n3">
                                                <form>
                                                    <select
                                                        className="form-select form-select-sm form-control-flush"
                                                        value={itemsPerPage}
                                                        onChange={handleItemsPerPageChange}
                                                    >
                                                        <option value="20">20 per page</option>
                                                        <option value="40">40 per page</option>
                                                        <option value="60">60 per page</option>
                                                        <option value="100">100 per page</option>

                                                    </select>
                                                </form>
                                            </div>
                                            <div className="col-auto">
                                                <Filters
                                                    filterFields={filterFields}
                                                    onApplyFilters={handleFilterSubmit}
                                                    getFilterService ={
                                                    activeTab === 'support'
                                                        ? ProductTicketService.getProductTicketFilter
                                                        : activeTab === 'subscription'
                                                            ? SubscriptionTicketService.getSubscriptionTicketFilter
                                                            : AnonymousTicketService.getAnonymousTicketFilter}
                                                    getDateFilterService={
                                                    activeTab === 'support'
                                                        ? ProductTicketService.getAll
                                                        : activeTab === 'subscription'
                                                            ? SubscriptionTicketService.getAll
                                                            : AnonymousTicketService.getAll}       
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-header">
                                        <div className="row align-items-center">
                                            <div className="col">
                                                <ul className="nav nav-tabs nav-tabs-sm nav-justified card-header-tabs">
                                                    <li className="nav-item">
                                                        <button
                                                            className={`nav-link ${activeTab === 'support' ? 'active text-primary' : ''}`}
                                                            onClick={() => setActiveTab('support')}
                                                        >
                                                            Basic Support
                                                        </button>
                                                    </li>
                                                    <li className="nav-item">
                                                        <button
                                                            className={`nav-link ${activeTab === 'subscription' ? 'active text-primary' : ''}`}
                                                            onClick={() => setActiveTab('subscription')}
                                                        >
                                                            Premium Support
                                                        </button>
                                                    </li>
                                                    <li className="nav-item">
                                                        <button
                                                            className={`nav-link ${activeTab === 'anonymous' ? 'active text-primary' : ''}`}
                                                            onClick={() => setActiveTab('anonymous')}
                                                        >
                                                            Anonymous Support
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="table-responsive">
                                        {
                                            activeTab === 'support' &&  

                                                <ListSupportTicket
                                                    rows={rows}
                                                    setRows={setRows}
                                                    statusClasses={statusClasses}
                                                    priorityClasses={priorityClasses}
                                                    handleAssign={handleAssign}
                                                    handleColumnChange={handleColumnChange}
                                                    handleStatusPriority={handleStatusPriority}
                                                    selectedTickets={selectedTickets}
                                                    onSelectTicket={onSelectTicket}
                                                    onSelectAll={onSelectAllTickets}
                                                    offset = {offset}
                                                />       
                                        }
                                        {activeTab === 'subscription' &&
                                            <ListSubscriptionPlanTicket
                                                statusClasses={statusClasses}
                                                priorityClasses={priorityClasses}
                                                handleAssign={handleAssign}
                                                handleColumnChange={handleColumnChange}
                                                handleStatusPriority={handleStatusPriority}
                                                rows={rows}
                                                setRows={setRows}
                                                selectedTickets={selectedTickets}
                                                onSelectTicket={onSelectTicket}
                                                onSelectAll={onSelectAllTickets}
                                                offset = {offset}
                                            />}
                                        {activeTab === 'anonymous' &&
                                            <ListAnonymousTicket
                                                statusClasses={statusClasses}
                                                priorityClasses={priorityClasses}
                                                handleAssign={handleAssign}
                                                handleColumnChange={handleColumnChange}
                                                handleStatusPriority={handleStatusPriority}
                                                rows={rows}
                                                setRows={setRows}
                                                selectedTickets={selectedTickets}
                                                onSelectTicket={onSelectTicket}
                                                onSelectAll={onSelectAllTickets}
                                                offset = {offset}
                                            />}

                                    </div>
                                            <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            setCurrentPage={setCurrentPage}
                                            />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showAssignModal && (
                <Assign
                    show={showAssignModal}
                    handleClose={handleClose}
                    onSubmit={onAssignSubmit}
                    handleSubmit={handleSubmit}
                    control={control}
                    errors={errors}
                    AssigneeList={usersList}
                />
            )}
            {showStatusPriorityModal && (
                <EditTicketModal
                    show={showStatusPriorityModal}
                    handleSubmit={handleSubmit}
                    handleClose={handleClose}
                    onSubmit={onStatusPrioritySubmit}
                    id={ticketId}
                    control={control}
                    errors={errors}
                    options={columnType === 'priority' ? priorityOptions : statusOptions}
                    type={columnType}
                />
            )}

            {showCreateProductTicket && (
                <CreateProductTicket
                    show={showCreateProductTicket}
                    customerId={customerId}
                    setCustomerId={setCustomerId}
                    priorityOptions={priorityOptions}
                    statusOptions={statusOptions}
                    handleClose={handleClose}
                    handleSubmit={handleSubmit}
                    register={register}
                    control={control}
                    errors={errors}
                    customerOptions={customerOptions}
                    onProductTicketSubmit={onTicketSubmit}
                    productOptions={productOptions}
                />
            )}
            {showSubscriptionTicket && (
                <CreateSubscriptionTicketModal
                    show={showSubscriptionTicket}
                    handleClose={handleClose}
                    onTicketSubmit={onTicketSubmit}
                    handleSubmit={handleSubmit}
                    control={control}
                    errors={errors}
                    setCustomerId={setCustomerId}
                    customerOptions={customerOptions}
                    planData={plan}
                />
            )}
            {showCreateAnonymousTicket && (
                <CreateUpdateAnonymousTicket
                    show={showCreateAnonymousTicket}
                    handleClose={handleClose}
                    onAnonymousTicketSubmit={onTicketSubmit}
                    handleSubmit={handleSubmit}
                    setCustomerId={setCustomerId}
                    customerOptions={customerOptions}
                    productOptions={productOptions}
                    customerId={customerId}
                    register={register}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                />
            )}
        </>
    )
}

export default Tickets
